(() => {
  'use strict';
  if (customElements.get('personalized-products')) return;

  const recentStorageKey = 'delifresh:recent:v1';
  const recentTtl = 30 * 24 * 60 * 60 * 1000;
  const day = 24 * 60 * 60 * 1000;
  const root = () =>
    (window.Shopify?.routes?.root || document.body.dataset.root || '/').replace(/\/?$/, '/');

  function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  function readRecent() {
    try {
      const data = JSON.parse(localStorage.getItem(recentStorageKey) || '[]');
      if (!Array.isArray(data)) return [];
      const seen = new Set();
      return data
        .filter((item) => {
          const handle = typeof item?.handle === 'string' ? item.handle.trim() : '';
          const id = String(item?.id || '').trim();
          if (
            !item ||
            !handle ||
            handle.length > 255 ||
            /[\\/?#\u0000-\u001f]/.test(handle) ||
            !/^\d+$/.test(id) ||
            !Number.isFinite(item.timestamp) ||
            Date.now() - item.timestamp > recentTtl ||
            seen.has(id)
          )
            return false;
          seen.add(id);
          return true;
        })
        .slice(0, 20)
        .map(({ handle, id, timestamp }) => ({
          handle: handle.trim(),
          id: String(id),
          timestamp,
        }));
    } catch {
      return [];
    }
  }

  function readPurchaseHistory(element) {
    try {
      const source = element.querySelector('[data-purchase-history]')?.textContent || '[]';
      const rows = JSON.parse(source);
      if (!Array.isArray(rows)) return [];
      return rows
        .map((row) => {
          const rawTimestamp = Number(row.timestamp);
          return {
            productId: String(row.productId || ''),
            orderId: String(row.orderId || ''),
            quantity: Math.max(0, Number(row.quantity) || 0),
            timestamp: rawTimestamp < 1e12 ? rawTimestamp * 1000 : rawTimestamp,
            productType: String(row.productType || ''),
            tags: Array.isArray(row.tags) ? row.tags.map(String) : [],
          };
        })
        .filter(
          (row) =>
            row.productId &&
            row.orderId &&
            row.quantity > 0 &&
            Number.isFinite(row.timestamp) &&
            row.timestamp > 0,
        );
    } catch {
      return [];
    }
  }

  function buildPurchaseStats(element) {
    const stats = new Map();
    for (const row of readPurchaseHistory(element)) {
      if (!stats.has(row.productId)) {
        stats.set(row.productId, {
          id: row.productId,
          orders: new Set(),
          quantity: 0,
          timestamps: new Set(),
          productType: row.productType,
          tags: row.tags,
        });
      }
      const item = stats.get(row.productId);
      item.orders.add(row.orderId);
      item.quantity += row.quantity;
      item.timestamps.add(row.timestamp);
      if (!item.productType && row.productType) item.productType = row.productType;
      if (!item.tags.length && row.tags.length) item.tags = row.tags;
    }
    return stats;
  }

  function expectedCycleDays(item) {
    const timestamps = [...item.timestamps].sort((a, b) => a - b);
    if (timestamps.length > 1) {
      const intervals = timestamps
        .slice(1)
        .map((timestamp, index) => (timestamp - timestamps[index]) / day)
        .filter((days) => days >= 2 && days <= 365);
      if (intervals.length) {
        return clamp(
          intervals.reduce((total, interval) => total + interval, 0) / intervals.length,
          7,
          180,
        );
      }
    }

    const explicitCycle = item.tags
      .map((tag) => tag.match(/(?:reorder|repeat|ซื้อซ้ำ)[\s:_-]?(\d{1,3})/i)?.[1])
      .find(Boolean);
    if (explicitCycle) return clamp(Number(explicitCycle), 7, 180);

    const type = [item.productType, ...item.tags].join(' ').toLowerCase();
    if (/เครื่องใช้ไฟฟ้า|เครื่องครัว|บ้านและไลฟ์สไตล์|appliance|cookware|kitchen|home/.test(type)) {
      return 120;
    }
    if (/ความงาม|อาหารเสริม|beauty|personal care|supplement/.test(type)) return 60;
    return 40;
  }

  function rankReorderProducts(element) {
    const grid = element.querySelector('[data-reorder-grid]');
    const empty = element.querySelector('[data-reorder-empty]');
    if (!grid) return 0;

    const cards = [...grid.querySelectorAll('.product-card[data-product-id]')];
    const stats = buildPurchaseStats(element);
    if (!cards.length || !stats.size) {
      if (empty) empty.hidden = false;
      return 0;
    }

    const bestsellers = String(element.dataset.bestsellers || '')
      .split(',')
      .filter(Boolean);
    const bestsellerRank = new Map(bestsellers.map((id, index) => [id, index]));
    const availableStats = cards.map((card) => stats.get(card.dataset.productId)).filter(Boolean);
    const maxFrequency = Math.max(1, ...availableStats.map((item) => item.orders.size));
    const maxQuantity = Math.max(1, ...availableStats.map((item) => item.quantity));
    const now = Date.now();

    const ranked = cards
      .map((card) => {
        const item = stats.get(card.dataset.productId);
        if (!item) return { card, score: -1, lastPurchasedAt: 0 };
        const lastPurchasedAt = Math.max(...item.timestamps);
        const daysSincePurchase = Math.max(0, (now - lastPurchasedAt) / day);
        const dueScore = clamp(daysSincePurchase / expectedCycleDays(item), 0, 1.25) / 1.25;
        const frequencyScore = item.orders.size / maxFrequency;
        const quantityScore = item.quantity / maxQuantity;
        const rank = bestsellerRank.get(item.id);
        const bestsellerScore =
          rank === undefined || bestsellers.length < 2 ? 0 : 1 - rank / (bestsellers.length - 1);
        return {
          card,
          lastPurchasedAt,
          score:
            frequencyScore * 0.4 +
            dueScore * 0.3 +
            quantityScore * 0.2 +
            bestsellerScore * 0.1,
        };
      })
      .sort((a, b) => b.score - a.score || b.lastPurchasedAt - a.lastPurchasedAt);

    const limit = Number(element.dataset.limit) || 4;
    grid.replaceChildren(...ranked.slice(0, limit).map((item) => item.card));
    if (empty) empty.hidden = ranked.length > 0;
    return Math.min(ranked.length, limit);
  }

  function rememberProduct(scope = document) {
    const product = scope.querySelector('product-detail[data-product-handle]');
    if (!product) return;
    const record = {
      handle: product.dataset.productHandle,
      id: String(product.dataset.productId || ''),
      timestamp: Date.now(),
    };
    if (!record.handle || !record.id) return;
    try {
      localStorage.setItem(
        recentStorageKey,
        JSON.stringify(
          [record, ...readRecent().filter((item) => item.id !== record.id)].slice(0, 20),
        ),
      );
    } catch {
      /* Storage may be unavailable; fallback products remain usable. */
    }
  }

  const cardCache = new Map();
  async function fetchCard(record, excludeUnavailable, signal) {
    const key = root() + record.handle;
    if (!cardCache.has(key)) {
      const promise = (async () => {
        const path = root() + 'products/' + encodeURIComponent(record.handle);
        const response = await fetch(path + '.js');
        if (!response.ok) return null;
        const product = await response.json();
        const cardResponse = await fetch(path + '?section_id=product-card-response');
        if (!cardResponse.ok) return null;
        const parsed = new DOMParser().parseFromString(await cardResponse.text(), 'text/html');
        return {
          available: product.available,
          card: parsed.querySelector('.product-card[data-product-id]'),
        };
      })().catch(() => {
        cardCache.delete(key);
        return null;
      });
      cardCache.set(key, promise);
    }
    const data = await cardCache.get(key);
    if (signal?.aborted || !data?.card || (excludeUnavailable && !data.available)) return null;
    return data.card.cloneNode(true);
  }

  let instance = 0;
  function uniqueIds(container) {
    const suffix = '-personal-' + ++instance;
    container.querySelectorAll('[id]').forEach((element) => {
      const oldId = element.id;
      element.id = oldId + suffix;
      container.querySelectorAll('[for],[form]').forEach((reference) => {
        for (const attribute of ['for', 'form']) {
          if (reference.getAttribute(attribute) === oldId) reference.setAttribute(attribute, element.id);
        }
      });
    });
  }

  async function fillRecent(element) {
    const grid = element.querySelector('[data-recent-grid]');
    if (!grid) return;
    element.recentRequest?.abort();
    element.recentRequest = new AbortController();
    const signal = element.recentRequest.signal;
    const current = element.dataset.current || document.querySelector('product-detail')?.dataset.productId;
    const records = readRecent().filter((item) => String(item.id) !== String(current));
    const limit = Number(element.dataset.limit) || 4;
    grid.setAttribute('aria-busy', 'true');
    const cards = [];

    for (let start = 0; start < records.length && cards.length < limit && !signal.aborted; start += 4) {
      const batch = await Promise.all(
        records
          .slice(start, start + 4)
          .map((record) => fetchCard(record, element.dataset.excludeUnavailable === 'true', signal)),
      );
      cards.push(...batch.filter(Boolean));
    }

    if (signal.aborted || !element.isConnected) return;
    grid.replaceChildren(...cards.slice(0, limit));
    uniqueIds(grid);
    grid.removeAttribute('aria-busy');
    const empty = element.querySelector('[data-recent-empty]');
    if (empty) empty.hidden = cards.length > 0;
    const clear = element.querySelector('[data-clear-recent]');
    if (clear) clear.hidden = records.length === 0;
  }

  function buildRecommendationSeeds(element) {
    const seeds = new Map();
    const add = (id, weight) => {
      const key = String(id || '');
      if (!key) return;
      seeds.set(key, (seeds.get(key) || 0) + weight);
    };

    const purchaseStats = [...buildPurchaseStats(element).values()].sort((a, b) => {
      const scoreA = a.orders.size * 2 + a.quantity;
      const scoreB = b.orders.size * 2 + b.quantity;
      return scoreB - scoreA || Math.max(...b.timestamps) - Math.max(...a.timestamps);
    });
    purchaseStats.slice(0, 3).forEach((item, index) => add(item.id, 2 - index * 0.2));
    readRecent()
      .slice(0, 3)
      .forEach((item, index) => add(item.id, 1.5 - index * 0.2));

    return [...seeds.entries()]
      .map(([id, weight]) => ({ id, weight }))
      .sort((a, b) => b.weight - a.weight)
      .slice(0, 4);
  }

  async function fetchRelatedCards(seed, element) {
    const params = new URLSearchParams({
      product_id: seed.id,
      limit: '10',
      section_id: 'recommendation-response',
      intent: 'related',
    });
    const response = await fetch(root() + 'recommendations/products?' + params, {
      signal: element.listeners.signal,
    });
    if (!response.ok) return [];
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...html.querySelectorAll('.product-card[data-product-id]')].filter((card) => {
      return element.dataset.excludeUnavailable !== 'true' || !card.querySelector('button:disabled');
    });
  }

  function chooseDiverseProducts(candidates, limit) {
    const selected = [];
    const selectedIds = new Set();
    const vendorCounts = new Map();
    const typeCounts = new Map();
    const canAdd = (candidate) => {
      const vendor = candidate.card.dataset.productVendor;
      const type = candidate.card.dataset.productType;
      return (!vendor || (vendorCounts.get(vendor) || 0) < 2) && (!type || (typeCounts.get(type) || 0) < 2);
    };
    const add = (candidate) => {
      selected.push(candidate);
      selectedIds.add(candidate.id);
      const vendor = candidate.card.dataset.productVendor;
      const type = candidate.card.dataset.productType;
      if (vendor) vendorCounts.set(vendor, (vendorCounts.get(vendor) || 0) + 1);
      if (type) typeCounts.set(type, (typeCounts.get(type) || 0) + 1);
    };

    for (const candidate of candidates) {
      if (selected.length >= limit) break;
      if (canAdd(candidate)) add(candidate);
    }
    for (const candidate of candidates) {
      if (selected.length >= limit) break;
      if (!selectedIds.has(candidate.id)) add(candidate);
    }
    return selected;
  }

  async function fillLikes(element) {
    if (element.dataset.recommend !== 'true') return;
    const grid = element.querySelector('[data-like-grid]');
    if (!grid) return;

    const seeds = buildRecommendationSeeds(element);
    if (!seeds.length) return;
    const excludedIds = new Set(seeds.map((seed) => seed.id));
    readRecent().forEach((item) => excludedIds.add(String(item.id)));
    buildPurchaseStats(element).forEach((_, id) => excludedIds.add(id));

    const candidates = new Map();
    const addCandidate = (card, score, contribution) => {
      const id = card.dataset.productId;
      if (!id || excludedIds.has(id)) return;
      const existing = candidates.get(id);
      if (existing) {
        existing.score += score;
        if (contribution > existing.bestContribution) {
          existing.card = card;
          existing.bestContribution = contribution;
        }
      } else {
        candidates.set(id, { id, card, score, bestContribution: contribution });
      }
    };

    const fallbackCards = [...grid.querySelectorAll('.product-card[data-product-id]')];
    fallbackCards.forEach((card, index) => {
      addCandidate(card, Math.max(0.02, 0.12 - index * 0.01), 0.12);
    });

    grid.setAttribute('aria-busy', 'true');
    try {
      const results = await Promise.allSettled(seeds.map((seed) => fetchRelatedCards(seed, element)));
      if (!element.isConnected) return;
      results.forEach((result, seedIndex) => {
        if (result.status !== 'fulfilled') return;
        const seed = seeds[seedIndex];
        result.value.forEach((card, rank) => {
          const contribution = seed.weight / (rank + 1);
          addCandidate(card, contribution, contribution);
        });
      });

      const ranked = [...candidates.values()].sort((a, b) => b.score - a.score);
      const limit = Number(element.dataset.limit) || 4;
      const selected = chooseDiverseProducts(ranked, limit);
      if (selected.length) {
        grid.replaceChildren(...selected.map((candidate) => candidate.card));
        uniqueIds(grid);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      /* Keep the 15-day best-seller fallback if recommendations fail. */
    } finally {
      grid.removeAttribute('aria-busy');
    }
  }

  class PersonalizedProducts extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      const reorderCount = rankReorderProducts(this);

      this.addEventListener(
        'click',
        (event) => {
          const tab = event.target.closest('[data-tab]');
          if (tab) this.activate(tab);
          if (event.target.closest('[data-clear-recent]')) clearHistory();
        },
        options,
      );
      this.addEventListener(
        'keydown',
        (event) => {
          const current = event.target.closest('[role=tab]');
          if (!current || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const tabs = [...this.querySelectorAll('[role=tab]')];
          let index = tabs.indexOf(current);
          if (event.key === 'Home') index = 0;
          else if (event.key === 'End') index = tabs.length - 1;
          else index = (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
          this.activate(tabs[index]);
          tabs[index].focus();
        },
        options,
      );
      document.addEventListener('delifresh:recent-cleared', () => fillRecent(this), options);

      const defaultKey = reorderCount > 0 ? this.dataset.defaultTab : 'like';
      const defaultTab = this.querySelector(`[data-tab="${defaultKey}"]`);
      if (defaultTab) this.activate(defaultTab);
    }

    activate(tab) {
      this.querySelectorAll('[role=tab]').forEach((element) => {
        const active = element === tab;
        element.setAttribute('aria-selected', String(active));
        element.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[role=tabpanel]').forEach((element) => {
        element.hidden = element.dataset.panel !== tab.dataset.tab;
      });
      if (tab.dataset.tab === 'recent') fillRecent(this);
      if (tab.dataset.tab === 'like' && !this.loadedLikes) {
        this.loadedLikes = true;
        fillLikes(this);
      }
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.recentRequest?.abort();
      this.ready = false;
    }
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.listeners = new AbortController();
      this.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('[data-clear-recent]')) clearHistory();
        },
        { signal: this.listeners.signal },
      );
      document.addEventListener('delifresh:recent-cleared', () => fillRecent(this), {
        signal: this.listeners.signal,
      });
      fillRecent(this);
    }

    disconnectedCallback() {
      this.listeners?.abort();
      this.recentRequest?.abort();
      this.ready = false;
    }
  }

  class Recommendations extends HTMLElement {
    async connectedCallback() {
      if (this.ready || !this.dataset.productId) return;
      this.ready = true;
      this.pending = new AbortController();
      const params = new URLSearchParams({
        product_id: this.dataset.productId,
        section_id: this.dataset.sectionId,
        limit: this.dataset.limit || '4',
        intent: 'related',
      });
      try {
        const response = await fetch(root() + 'recommendations/products?' + params, {
          signal: this.pending.signal,
        });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const next = html.querySelector('[data-recommendations-grid]');
        if (next?.children.length && this.isConnected) {
          this.querySelector('[data-recommendations-grid]').replaceChildren(...next.children);
        }
      } catch {
        /* Preserve server-rendered fallback. */
      }
      if (!this.querySelector('.product-card')) {
        this.closest('.shopify-section')?.setAttribute('hidden', '');
      }
    }

    disconnectedCallback() {
      this.pending?.abort();
      this.ready = false;
    }
  }

  function clearHistory() {
    try {
      localStorage.removeItem(recentStorageKey);
    } catch {}
    document.dispatchEvent(new CustomEvent('delifresh:recent-cleared'));
  }

  rememberProduct();
  document.addEventListener('shopify:section:load', (event) => rememberProduct(event.target));
  customElements.define('personalized-products', PersonalizedProducts);
  customElements.define('recently-viewed-products', RecentlyViewed);
  customElements.define('product-recommendations', Recommendations);
})();
